import { EventOperationsState, EventPortfolioState } from '../types';
import { createBlankEventOperationsState } from './eventExperience';
import { normalizeEventOperationsState } from './eventOperations';

const nowIso = () => new Date().toISOString();

const normalizeEvent = (value: EventOperationsState): EventOperationsState => {
  if (value?.dataSource === 'demo') {
    return createBlankEventOperationsState(undefined, value?.event?.id || `event-${Date.now()}`);
  }
  const fallback = createBlankEventOperationsState(undefined, value?.event?.id || `event-${Date.now()}`);
  return normalizeEventOperationsState(value, fallback);
};

export function createEventPortfolioState(
  seed?: EventOperationsState,
  timestamp = nowIso(),
): EventPortfolioState {
  const initial = normalizeEvent(seed ?? createBlankEventOperationsState(timestamp));
  return {
    version: 1,
    activeEventId: initial.event.id,
    events: [initial],
    updatedAt: timestamp,
  };
}

export function normalizeEventPortfolioState(
  value: Partial<EventPortfolioState> | null | undefined,
  legacyEvent?: EventOperationsState | null,
): EventPortfolioState {
  const candidates = Array.isArray(value?.events)
    ? value.events.filter((event): event is EventOperationsState => Boolean(event?.event?.id))
    : [];
  const events = candidates.length
    ? candidates.map(normalizeEvent)
    : legacyEvent
      ? [normalizeEvent(legacyEvent)]
      : [createBlankEventOperationsState()];
  const requestedActiveId = value?.activeEventId;
  const activeEventId = events.some((event) => event.event.id === requestedActiveId)
    ? requestedActiveId as string
    : events[0].event.id;
  return {
    version: 1,
    activeEventId,
    events,
    updatedAt: value?.updatedAt || nowIso(),
  };
}

export function getActiveEvent(portfolio: EventPortfolioState): EventOperationsState {
  return portfolio.events.find((event) => event.event.id === portfolio.activeEventId)
    ?? portfolio.events[0]
    ?? createBlankEventOperationsState();
}

export function replaceActiveEvent(
  portfolio: EventPortfolioState,
  nextEvent: EventOperationsState,
): EventPortfolioState {
  const normalized = normalizeEvent(nextEvent);
  const hasActive = portfolio.events.some((event) => event.event.id === portfolio.activeEventId);
  const events = hasActive
    ? portfolio.events.map((event) => event.event.id === portfolio.activeEventId ? normalized : event)
    : [...portfolio.events, normalized];
  return { ...portfolio, activeEventId: normalized.event.id, events, updatedAt: nowIso() };
}

export function addEventToPortfolio(
  portfolio: EventPortfolioState,
  event = createBlankEventOperationsState(),
): EventPortfolioState {
  const normalized = normalizeEvent(event);
  return {
    ...portfolio,
    activeEventId: normalized.event.id,
    events: [...portfolio.events, normalized],
    updatedAt: nowIso(),
  };
}

export function selectEventInPortfolio(
  portfolio: EventPortfolioState,
  eventId: string,
): EventPortfolioState {
  if (!portfolio.events.some((event) => event.event.id === eventId)) return portfolio;
  return { ...portfolio, activeEventId: eventId, updatedAt: nowIso() };
}

export function removeEventFromPortfolio(
  portfolio: EventPortfolioState,
  eventId: string,
): EventPortfolioState {
  const remaining = portfolio.events.filter((event) => event.event.id !== eventId);
  if (!remaining.length) return createEventPortfolioState();
  return {
    ...portfolio,
    events: remaining,
    activeEventId: portfolio.activeEventId === eventId ? remaining[0].event.id : portfolio.activeEventId,
    updatedAt: nowIso(),
  };
}
